import React, { useMemo } from "react";
import { OperatorSelectorProps } from "react-querybuilder";
import colors from "app/colors";
import { Select, SingleValue } from "chakra-react-select";
import { isOptionGroup } from "modules/utils/Functions";

interface OptionType {
  value: string;
  label: string;
}

const QueryOperatorSelector: React.FC<OperatorSelectorProps> = ({
  handleOnChange,
  value,
  options,
  className,
}) => {
  const selectOptions = useMemo(
    () =>
      options.map((option) => {
        if (isOptionGroup(option)) {
          return {
            label: option.label,
            options: option.options.map((subOption) => ({
              value: subOption.name,
              label: subOption.label,
            })),
          };
        }
        return {
          value: option.name,
          label: option.label,
        };
      }),
    [options],
  );

  // Get the selected value object
  const selectedOption = useMemo(() => {
    const flatten = (opts: any[]): OptionType[] =>
      opts.flatMap((opt) => ("options" in opt ? opt.options : [opt]));
    return flatten(selectOptions).find((opt) => opt.value === value) ?? null;
  }, [value, selectOptions]);

  // Handle change from chakra-react-select
  const onChange = (selected: SingleValue<OptionType>) => {
    handleOnChange(selected?.value ?? "");
  };

  return (
    <Select<OptionType, false>
      className={className}
      options={selectOptions}
      value={selectedOption}
      onChange={onChange}
      isClearable={false}
      placeholder="Select operator..."
      size="lg"
      components={{ IndicatorSeparator: () => null }}
      chakraStyles={{
        container: (provided) => ({
          ...provided,
          width: "285px",
        }),
        control: (provided) => ({
          ...provided,
          minHeight: "40px",
          borderRadius: "8px",
          backgroundColor: colors.white,
          fontSize: "14px",
        }),
        singleValue: (provided) => ({
          ...provided,
          fontSize: "14px",
        }),
        option: (provided) => ({
          ...provided,
          fontSize: "14px",
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          bg: colors.white,
          color: colors.gray40,
          width: "20px",
        }),
      }}
    />
  );
};

export default QueryOperatorSelector;
